import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";

// Admin authorization is enforced server-side via public.has_role against the
// user_roles table, gated in /_authenticated/admin (route layout). This page
// is just a sign-in form — it cannot grant admin to anyone.
export const Route = createFileRoute("/admin-auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin Access — The Hospitality Reserve" },
      { name: "description", content: "Restricted administrator sign-in for The Hospitality Reserve operations team." },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/admin-auth" }],
  }),
  component: AdminAuthPage,
});

function AdminAuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) throw error;
      navigate({ to: "/admin" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 lg:px-16">
        <Link to="/" className="inline-block">
          <p className="tracking-wide-luxury text-accent">The Hospitality</p>
          <p className="font-display text-lg">Reserve</p>
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="tracking-wide-luxury text-accent">Administration</p>
            <span className="gold-divider mt-6" />
            <h1 className="mt-6 font-display text-3xl">Admin sign in.</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Restricted to authorised personnel only. Access is verified server-side.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 bg-white text-foreground p-8">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-2"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/reset-password"
                  className="text-xs tracking-wide-luxury text-accent hover:text-accent/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password" required minLength={8} autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-2"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6"
            >
              {loading ? "Please wait…" : "Sign In"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
